import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Play } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import { marketplaceTitle } from "@/api/returns";
import * as api from "@/api/system_settings";
import * as sync from "@/api/stock_sync";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toastError } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";
import { mskDateTime, skipTitle } from "@/pages/settings/stock-sync-preview";
import type { SystemSettingsUpdate } from "@/api/system_settings";
import type { SystemSettingsView } from "@/types/api";

export const STOCK_SYNC_STATUS_KEY = ["stock-sync"] as const;

/** Сколько строк расхождений показываем; остальное — числом. */
const OVER_ROWS_SHOWN = 25;

const MARKETPLACES = [
  { code: "wb", setting: "stock_sync_wb_enabled" },
  { code: "ozon", setting: "stock_sync_ozon_enabled" },
] as const;

/**
 * Синхронизация остатков ФБС: общие переключатели.
 *
 * ⚠️ Это первое место, где портал начинает МЕНЯТЬ витрину маркетплейса сам,
 * поэтому остановить можно одним тапом, а сухой прогон — состояние по
 * умолчанию: выкат новой версии не должен сам по себе начать писать на
 * маркетплейсы. Склады — `StockSyncWarehousesCard`, расхождения —
 * `StockSyncDiscrepancyCard` (на вкладке — именно в этом порядке).
 */
export function StockSyncCard({
  settings,
}: {
  settings: SystemSettingsView;
}): React.ReactElement {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: STOCK_SYNC_STATUS_KEY, queryFn: sync.status });

  const save = useMutation({
    mutationFn: (patch: SystemSettingsUpdate) => api.updateSystemSettings(patch),
    onSuccess: () => {
      toast.success("Сохранено");
      void qc.invalidateQueries({ queryKey: ["system-settings"] });
      void qc.invalidateQueries({ queryKey: STOCK_SYNC_STATUS_KEY });
    },
    onError: toastError("Не удалось сохранить"),
  });

  const run = useMutation({
    mutationFn: sync.runNow,
    onSuccess: (data) => {
      qc.setQueryData(STOCK_SYNC_STATUS_KEY, data);
      const last = data.last_runs[0];
      toast.success(
        last
          ? `Прогон выполнен: позиций ${last.positions}, изменений ${last.changed}, ` +
              (last.dry_run
                ? "на маркетплейсы не отправлено (сухой прогон)"
                : `опубликовано ${last.published}`)
          : "Прогон выполнен",
      );
    },
    onError: toastError("Не удалось запустить прогон"),
  });

  const s = statusQ.data;
  const on = MARKETPLACES.filter((m) => settings[m.setting]).map((m) => marketplaceTitle(m.code));
  const off = MARKETPLACES.filter((m) => !settings[m.setting]).map((m) =>
    marketplaceTitle(m.code),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Остатки ФБС на маркетплейсах</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Портал рассчитывает свободный остаток каждого склада 1С — за вычетом собранного, но
          ещё не проведённого, и несобранных заказов — и публикует его на склады
          маркетплейсов, привязанные к этому складу 1С. Какие товары публиковать, задаётся на
          каждом складе маркетплейса.
        </p>

        {s && !s.one_c_ready && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="mr-1 inline size-4" />
            Не настроен служебный доступ к 1С — синхронизация работать не будет.
            <div className="mt-1 text-xs">{s.one_c_error}</div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.stock_sync_enabled}
              onChange={(e) => save.mutate({ stock_sync_enabled: e.target.checked })}
            />
            Синхронизация включена
          </label>
          {/* ⚠️ Именно «сухой прогон», а не «отправлять»: положительная формулировка
              делала бы выключенное состояние неотличимым от «ещё не настроили». */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.stock_sync_dry_run}
              onChange={(e) => save.mutate({ stock_sync_dry_run: e.target.checked })}
            />
            Сухой прогон — рассчитывать без отправки на маркетплейсы
          </label>
          {/* Выключенный маркетплейс не рассчитывается, не читается и не пишется даже
              в сухом прогоне (его API не трогаем вовсе), и цикл не загружает его
              заказы; но его спрос из уже загруженных заказов вычитается из
              общего остатка — иначе другой маркетплейс продаст обещанное ему. */}
          {MARKETPLACES.map((m) => (
            <label key={m.code} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings[m.setting]}
                onChange={(e) => save.mutate({ [m.setting]: e.target.checked })}
              />
              Публиковать на {marketplaceTitle(m.code)}
            </label>
          ))}
          <NumberField
            label="Интервал между прогонами, с"
            value={settings.stock_sync_interval_seconds}
            min={30}
            error="Интервал не может быть меньше 30 с — это ограничение Ozon."
            onSave={(v) => save.mutate({ stock_sync_interval_seconds: v })}
          />
          <NumberField
            label="Макс. изменение за прогон, шт."
            value={settings.stock_sync_max_delta}
            min={1}
            error="Значение должно быть не меньше 1: при 0 не публиковалось бы ни одно изменение."
            onSave={(v) => save.mutate({ stock_sync_max_delta: v })}
          />
          <Button size="sm" variant="outline" onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? (
              <Loader2 className="mr-1 size-4 animate-spin" />
            ) : (
              <Play className="mr-1 size-4" />
            )}
            Запустить прогон
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Изменение остатка больше допустимого публикуется, только если следующий прогон
          рассчитает то же значение.
        </p>

        {off.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Не публикуется: {off.join(", ")}. Остатки не рассчитываются и не отправляются,
            заказы автоматически не загружаются (только синхронизацией в разделе ФБС). Уже
            загруженные заказы по-прежнему уменьшают свободный остаток.
          </p>
        )}

        {!settings.stock_sync_dry_run && on.length > 0 && (
          <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mr-1 inline size-4" />
            Сухой прогон выключен — портал сам изменяет остатки на {on.join(" и ")}.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Где остаток на маркетплейсе больше расчётного — по последнему прогону.
 *
 * ⚠️ Это СОСТОЯНИЕ последнего прогона, не живой расчёт: дата прогона стоит над
 * таблицей всегда, а выключенный цикл назван прямо — иначе вчерашние числа
 * выглядели бы текущими. Бэкенд отдаёт только включённые склады и отмеченные
 * товары: остальное синхронизация держит нулём или не трогает.
 */
export function StockSyncDiscrepancyCard({
  settings,
}: {
  settings: SystemSettingsView;
}): React.ReactElement {
  const q = useQuery({ queryKey: STOCK_SYNC_STATUS_KEY, queryFn: sync.status });
  const s = q.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Расхождения с маркетплейсами</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading || q.error || !s ? (
          <LoadState
            isLoading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            className="flex flex-col items-center gap-3 p-4 text-center"
          />
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              {s.last_run_at ? `По прогону от ${mskDateTime(s.last_run_at)}` : "Прогонов ещё не было"}
            </div>
            {!settings.stock_sync_enabled && (
              <div className="rounded-md border border-amber-400 bg-amber-50 p-2 text-sm text-amber-900">
                <AlertTriangle className="mr-1 inline size-4" />
                Синхронизация выключена — данные не обновляются
              </div>
            )}
            {s.enabled_warehouses === 0 ? (
              <p className="text-sm text-muted-foreground">
                Ни один склад маркетплейса не включён — выберите товары и включите склад выше.
              </p>
            ) : (
              <Discrepancies status={s} />
            )}
            <LastRuns runs={s.last_runs} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Discrepancies({ status: s }: { status: sync.StockSyncStatus }): React.ReactElement {
  const over = s.rows.filter((r) => r.over > 0);
  return (
    <>
      <div className="text-sm">
        Товаров к публикации: <b>{s.rows_total}</b>
      </div>
      {s.over_total > 0 ? (
        <div className="text-sm text-destructive">
          Остаток на маркетплейсах превышает расчётный: <b>{s.over_total}</b> шт. по{" "}
          {s.over_positions} позициям
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          Остаток на маркетплейсах не превышает расчётный.
        </div>
      )}
      {over.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="align-bottom">
                <th className="py-1 pr-2">Склад маркетплейса</th>
                <th className="pr-2">Склад 1С</th>
                <th className="pr-2">Артикул</th>
                <th className="pr-2 text-right">На маркетплейсе</th>
                <th className="pr-2 text-right">Расчётный остаток</th>
                <th className="text-right">Превышение</th>
              </tr>
            </thead>
            <tbody>
              {over.slice(0, OVER_ROWS_SHOWN).map((r) => (
                <tr
                  key={`${r.marketplace}:${r.warehouse_key}:${r.unit_key}`}
                  className="border-t align-top"
                >
                  <td className="py-1 pr-2">
                    {marketplaceTitle(r.marketplace)} · {r.warehouse_name ?? r.warehouse_key}
                    <span className="block text-xs text-muted-foreground">
                      {r.cabinet_name ?? r.warehouse_note}
                    </span>
                  </td>
                  <td className="pr-2">{r.one_c_warehouse_name ?? "—"}</td>
                  <td className="pr-2">
                    {r.article || r.unit_key}
                    {r.skip_reason && (
                      <span className="block text-xs text-muted-foreground">
                        не опубликовано: {skipTitle(r.skip_reason)}
                      </span>
                    )}
                  </td>
                  <td className="pr-2 text-right tabular-nums">{r.platform_qty ?? "—"}</td>
                  <td className="pr-2 text-right tabular-nums">{r.computed_qty}</td>
                  <td className="text-right font-medium tabular-nums text-destructive">{r.over}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {over.length > OVER_ROWS_SHOWN && (
            <p className="pt-1 text-xs text-muted-foreground">
              Показаны {OVER_ROWS_SHOWN} позиций с наибольшим превышением из {s.over_positions}.
            </p>
          )}
        </div>
      )}
    </>
  );
}

function LastRuns({ runs }: { runs: sync.StockSyncRun[] }): React.ReactElement | null {
  if (runs.length === 0) return null;
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground">
      <div>Последние прогоны:</div>
      {runs.slice(0, 5).map((r) => (
        <div key={r.started_at}>
          {mskDateTime(r.started_at)} — позиций {r.positions}, изменений {r.changed},{" "}
          {r.dry_run ? "сухой прогон" : `опубликовано ${r.published}`}
          {r.error && <span className="text-destructive"> · {r.error}</span>}
          {/* Частичный отказ: ошибка — сводка, подробности — в замечаниях. */}
          {r.notes && <span> · {r.notes}</span>}
        </div>
      ))}
    </div>
  );
}

/**
 * Число-настройка с нижней границей.
 *
 * ⚠️ Пустое поле даёт `Number("") === 0`, а ноль в потолке отбивал бы КАЖДУЮ
 * позицию как «скачок больше потолка» — публикация замерла бы молча и
 * выглядела бы как исправная работа. Поэтому граница проверяется и здесь, и на
 * бэкенде: экранное правило обходится curl'ом.
 */
function NumberField({
  label,
  value,
  min,
  error,
  onSave,
}: {
  label: string;
  value: number;
  min: number;
  error: string;
  onSave: (v: number) => void;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-2 text-sm">
      {label}
      <Input
        type="number"
        className="w-24"
        defaultValue={value}
        onBlur={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v) && v >= min) {
            onSave(v);
          } else {
            e.target.value = String(value);
            toast.error(error);
          }
        }}
      />
    </label>
  );
}
